import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
	TextField,
} from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	useBarrels,
	useChangeRollsetBarrel,
	useValidateBarrelChange,
} from "../hooks/useRollshopOperations";
import type { RollsetListItem } from "../models/RollshopOperations";
import type { BarrelOption } from "../repositories/RollshopOperationsRepository";
import {
	COMPACT_DIALOG_SX,
	COMPACT_OPTION_SX,
	EDITABLE_FIELD_SX,
} from "./compactDialog";
import { ConfirmActionDialog } from "./ConfirmActionDialog";
import { ReadOnlyField } from "./ReadOnlyField";

interface Props {
	open: boolean;
	rollset: RollsetListItem | null;
	onClose: () => void;
	onChanged: () => void;
}

export const ChangeBarrelModal = ({
	open,
	rollset,
	onClose,
	onChanged,
}: Props) => {
	const [barrel, setBarrel] = useState<BarrelOption | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (open) {
			setBarrel(null);
			setConfirmOpen(false);
			setErrorMsg("");
		}
	}, [open]);

	const barrelsQuery = useBarrels();
	const validation = useValidateBarrelChange();
	const { changeBarrel, isChangingBarrel } = useChangeRollsetBarrel({
		onSuccess: () => {
			setConfirmOpen(false);
			onChanged();
		},
		onError: (e) => {
			setConfirmOpen(false);
			setErrorMsg(e.message);
		},
	});

	// Legacy: only the barrels of the rollset's stand.
	const options = useMemo(
		() =>
			(barrelsQuery.data ?? [])
				.filter(
					(b) =>
						rollset?.StandId != null &&
						b.StandId === rollset.StandId &&
						b.Id !== rollset.BarrelId,
				)
				.sort((a, b) => a.Name.localeCompare(b.Name)),
		[barrelsQuery.data, rollset],
	);

	const isSaving = validation.isPending || isChangingBarrel;

	const apply = () => {
		if (!rollset || !barrel) return;
		changeBarrel({ rollsetId: rollset.Id, barrelId: barrel.Id });
	};

	const handleSubmit = () => {
		if (!rollset || !barrel) return;
		setErrorMsg("");
		validation.mutate(
			{ rollsetId: rollset.Id, barrelId: barrel.Id },
			{
				onSuccess: (result) => {
					if (result === "WARNING") setConfirmOpen(true);
					else if (result === "SUCCESS") apply();
					else setErrorMsg("The barrel change could not be validated.");
				},
				onError: (e) => setErrorMsg(e.message),
			},
		);
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DetailHeader
					title={rollset?.BarrelId != null ? "Change Barrel" : "Set Barrel"}
					isSaving={isSaving}
					onClose={onClose}
				/>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<DetailSection title="Barrel" icon={<LayersOutlinedIcon />}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<ReadOnlyField label="Rollset" value={rollset?.Name} />
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<ReadOnlyField label="Stand" value={rollset?.StandName} />
									</Grid2>
									{rollset?.BarrelId != null && (
										<Grid2 size={12}>
											<ReadOnlyField
												label="Current Barrel"
												value={rollset.BarrelName}
											/>
										</Grid2>
									)}
									<Grid2 size={12}>
										<Autocomplete
											options={options}
											value={barrel}
											autoHighlight
											size="small"
											fullWidth
											loading={barrelsQuery.isFetching}
											getOptionLabel={(option) => option.Name}
											isOptionEqualToValue={(option, current) =>
												option.Id === current.Id
											}
											onChange={(_event, option) => {
												setBarrel(option);
												setErrorMsg("");
											}}
											renderOption={(props, option) => {
												const { key, ...rest } = props as typeof props & {
													key: string;
												};
												return (
													<li
														key={option.Id}
														{...rest}
														style={COMPACT_OPTION_SX}
													>
														{option.Name}
													</li>
												);
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													sx={EDITABLE_FIELD_SX}
													label="New Barrel"
													required
													helperText={
														!barrelsQuery.isFetching && options.length === 0
															? "No other barrels for this stand"
															: undefined
													}
													slotProps={{ inputLabel: { shrink: true } }}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>
							{errorMsg && <Alert severity="error">{errorMsg}</Alert>}
						</Box>
					</DialogContent>
					<Divider />
					<DialogActions sx={{ px: 2, py: 1.5 }}>
						<FooterModalButtons
							isSubmitLoading={isSaving}
							disabled={barrel == null}
							onHide={onClose}
							buttonLabel="Save"
						/>
					</DialogActions>
				</form>
			</Dialog>
			<ConfirmActionDialog
				open={confirmOpen}
				title="Change Barrel"
				message="Some production records will be deleted because barrels layout do not match exactly. Do you want to continue?"
				confirmLabel="Continue"
				confirmColor="warning"
				isSaving={isChangingBarrel}
				onConfirm={apply}
				onClose={() => setConfirmOpen(false)}
			/>
		</>
	);
};
